import { useAuth } from "@/src/contexts/auth.context";
import { useAllBlocksPermissionsQuery } from "@/src/services/uam/permissions.blocks.api";
import { PERMISSIONS, type PermissionResource } from "@/src/constants/permissions.constants";
import { useMemo } from "react";

const normalizeRole = (role?: string | null) =>
  (role ?? "").toLowerCase().replace(/[\s_-]/g, "");


export const usePermissions = () => {
  const { roles, isSuperAdmin, isSessionResolved } = useAuth();
  const { data, isLoading, isError } = useAllBlocksPermissionsQuery();

  const userRoles = useMemo(() => roles.map(normalizeRole), [roles]);

  // permission name → set of normalized roles that hold it.
  const rolesByPermission = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const permission of data?.data ?? []) {
      map.set(
        permission.name,
        new Set((permission.roles ?? []).map(normalizeRole)),
      );
    }
    return map;
  }, [data]);

  const can = (permissionName: string): boolean => {
    if (isSuperAdmin) return true;
    // The session has not resolved, so `roles` is empty because it is unknown,
    // not because the user holds none. Denying here rendered "Access restricted"
    // over perfectly permitted screens whenever the identity call hiccuped.
    if (!isSessionResolved) return true;
    if (isLoading || isError || !data) return true; // fail-open
    const allowedRoles = rolesByPermission.get(permissionName);
    if (!allowedRoles) return true; // permission not defined → unrestricted
    // Defined but attached to NO role = unconfigured, not "deny everyone".
    //
    // This is a real state, not a hypothetical: the app's 20 permissions were
    // created in Blocks on 2026-07-30, but `roles/assign-permissions` rejects
    // every role in this project with `Can_Not_Change_Default_role` (they all
    // report `createdFromDefault: true`, including a freshly created one). So the
    // catalog now contains `view-post` … `delete-wiki` with `roles: []`.
    //
    // Without this guard an empty Set is not `undefined`, so `can()` returned
    // false and every gate in the app slammed shut for everyone except
    // super-admins — a permission nobody can be granted yet must not lock the
    // product. Enforcement begins the moment roles are actually attached.
    if (allowedRoles.size === 0) return true;
    return userRoles.some((role) => allowedRoles.has(role));
  };

  // Resource-scoped helpers, e.g. canCreate("post"), canView("news").
  const canView = (resource: PermissionResource) =>
    can(PERMISSIONS[resource].view);
  const canCreate = (resource: PermissionResource) =>
    can(PERMISSIONS[resource].create);
  const canUpdate = (resource: PermissionResource) =>
    can(PERMISSIONS[resource].update);
  const canDelete = (resource: PermissionResource) =>
    can(PERMISSIONS[resource].delete);

  return {
    can,
    canView,
    canCreate,
    canUpdate,
    canDelete,
    isLoading,
    isSuperAdmin,
  };
};
